import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventBySlug } from '../data/events';

const EventDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const event = slug ? getEventBySlug(slug) : undefined;

  if (!event) {
    return (
      <div className="bg-[#080808] text-white min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-white mb-4">404</h1>
          <p className="text-xl text-gray-400 mb-8">Fant ikke arrangementet</p>
          <Link to="/events" className="btn-primary py-3 px-8 font-bold">
            Tilbake til arrangementer
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#080808] text-white min-h-screen section-padding overflow-hidden">
      <div className="container-responsive max-w-4xl">
        {/* Back Link */}
        <Link
          to="/events"
          className="inline-flex items-center gap-2 link-secondary text-sm mb-8 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
          </svg>
          Tilbake til arrangementer
        </Link>

        {/* Hero Section */}
        <div className="card-glow p-8 sm:p-10 mb-8">
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-6">
            <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 text-white opacity-25">
              {event.iconType === 'running' ? (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ) : (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
                </svg>
              )}
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
                {event.name}
              </h1>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-6 text-gray-400">
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                  </svg>
                  {event.date}
                </span>
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                  </svg>
                  {event.location}
                </span>
              </div>
            </div>
          </div>

          <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
            {event.longDescription}
          </p>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8">
          {/* Event Details */}
          <div className="card-glow p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-5 tracking-tight">
              Detaljer
            </h2>
            <dl className="space-y-4">
              {event.details.map((detail) => (
                <div key={detail.label}>
                  <dt className="text-sm text-gray-500 uppercase tracking-wider mb-1">
                    {detail.label}
                  </dt>
                  <dd className="text-base text-gray-200">
                    {detail.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Schedule */}
          <div className="card-glow p-6 sm:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-5 tracking-tight">
              Program
            </h2>
            <div className="space-y-4">
              {event.schedule.map((item, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-16 text-sm font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {item.time}
                  </div>
                  <div className="flex-1">
                    <div className="h-px mt-2 mb-2 hidden sm:block" style={{ background: 'rgba(255,255,255,0.07)' }} />
                    <p className="text-gray-300 text-base">{item.activity}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="card-glow p-6 sm:p-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            Interessert?
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            Datoer og detaljer publiseres snart. Følg med for oppdateringer!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary py-3 px-8 font-bold text-center">
              Kontakt oss
            </Link>
            <Link to="/events" className="btn-secondary py-3 px-8 font-bold text-center">
              Se alle arrangementer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
